// react
import styled, { css } from 'styled-components';

// components
import { Button } from '@/components/atoms/Button/Button';
import { Container } from '@/components/atoms/Container/Container';
import { Icon } from '@/components/atoms/Icon/Icon';
import { Card } from '@/components/atoms/Card/Card';

import { FloatingActions } from '@/components/molecules/FloatingActions/FloatingActions';

/**
 * AttendanceAllowanceSuccessPage page is displayed when the user has successfully submitted the Attendance Allowance form.
 */
export const AttendanceAllowanceSuccess = ({
  name,
  onBack,
  handlePrintPdf,
  isDownloading = false,
}: AttendanceAllowanceSuccessProps) => {
  return (
    <MainWrapper>
      {onBack && (
        <Button
          ariaLabel='back'
          iconLeft='chevron-left'
          variant='secondary'
          size='sm'
          onPress={onBack}
        >
          Back
        </Button>
      )}

      <TitleWrapper>
        <StyledTitle>{name}'s Attendance Allowance</StyledTitle>
      </TitleWrapper>

      <Container size='md'>
        <Wrapper>
          <WrapperHeader>
            <ImageWrapper>
              <Icon icon='truck-fast' fontSize={42} />
            </ImageWrapper>
            <HeaderTitle> Your printed form is on its way to you!</HeaderTitle>
          </WrapperHeader>

          <Card key='card-content' color='blue' isBordered={false}>
            <Content>
              <ContentTitle>Your next steps</ContentTitle>
              <table>
                <tbody>
                  <tr>
                    <StyledTd>
                      <StyledIcon icon='envelope-open-text' fontSize={22} />
                    </StyledTd>
                    <StyledTd>
                      Wait for your printed form to arrive by 1st class post.
                      This will arrive at your loved one’s address provided in
                      the form.
                    </StyledTd>
                  </tr>
                  <tr>
                    <StyledTd>
                      <StyledIcon icon='pen-field' fontSize={22} />
                    </StyledTd>
                    <StyledTd>
                      Tick one of the consent options and sign with a pen, on
                      page 9 under{' '}
                      <DarkText>
                        <i>Consent</i> (question 20). This lets the Department
                        for Work and Pensions contact the people named in the
                        form.
                      </DarkText>
                    </StyledTd>
                  </tr>
                  <tr>
                    <StyledTd>
                      <StyledIcon icon='pen-field' fontSize={22} />
                    </StyledTd>
                    <StyledTd>
                      Sign the form with a pen, on page 30 under{' '}
                      <DarkText>
                        {' '}
                        <i>Declaration</i> (question 64). This can either be
                        signed directly by your loved one, or by you as the
                        caregiver (if you have power of attorney).
                      </DarkText>{' '}
                    </StyledTd>
                  </tr>
                  <tr>
                    <StyledTd>
                      <StyledIcon icon='folder-open' fontSize={22} />
                    </StyledTd>
                    <StyledTd>
                      Collect any supporting evidence for your application.{' '}
                      <LinkButton
                        variant='link'
                        size='lg'
                        isInline
                        ariaLabel='here'
                        to={'/library/special-rules-for-attendance-allowance'}
                      >
                        Here
                      </LinkButton>{' '}
                      are some tips on finding your evidence.
                    </StyledTd>
                  </tr>
                  <tr>
                    <StyledTd>
                      <StyledIcon icon='mailbox' fontSize={22} />
                    </StyledTd>
                    <StyledTd>
                      Place the form and supporting evidence in your Freepost
                      envelope and deliver to the post box. You do not need a
                      postcode or a stamp.
                    </StyledTd>
                  </tr>
                  <tr>
                    <StyledTd>
                      <StyledIcon icon='calendar-check' fontSize={22} />
                    </StyledTd>
                    <StyledTd>
                      Wait for 30 days to hear about your claim from the
                      Department of Work and Pensions. If the claim is made
                      under the special rules for end of life, it will usually
                      be dealt with within 2 weeks.
                    </StyledTd>
                  </tr>
                  <tr>
                    <StyledTd>
                      <StyledIcon icon='phone' fontSize={22} />
                    </StyledTd>
                    <StyledTd>
                      You can check your application’s status by calling the
                      government attendance allowance helpline on{' '}
                      <PhoneNumber href='tel:08007310122'>
                        0800 731 0122
                      </PhoneNumber>
                    </StyledTd>
                  </tr>
                </tbody>
              </table>
            </Content>
          </Card>

          <FloatingActions
            actions={[
              {
                label: 'Back',
                onPress: onBack,
                variant: 'secondary',
              },
              {
                label: isDownloading ? 'Downloading...' : 'Download Form',
                onPress: handlePrintPdf,
                isDisabled: isDownloading,
              },
            ]}
          />
        </Wrapper>
      </Container>
    </MainWrapper>
  );
};

const MainWrapper = styled.div`
  margin-bottom: ${(props) => props.theme.spacing[56]};
`;

const TitleWrapper = styled.div`
  wigth: 100%;
  justify-content: center;
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
  border-bottom: 1px solid ${(props) => props.theme.color.neutral[30]};
`;

const WrapperHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${(props) => props.theme.spacing[16]};
  margin-top: ${(props) => props.theme.spacing[20]};
  margin-bottom: ${(props) => props.theme.spacing[16]};
`;

const HeaderTitle = styled.div`
  color: ${(props) => props.theme.color.neutral[100]};
  font: ${(props) => props.theme.typography.heading['3xl']};
`;

const StyledTitle = styled.div`
  color: ${(props) => props.theme.color.primary[80]};
  font: ${(props) => props.theme.typography.heading['4xl']};
  margin-bottom: ${(props) => props.theme.spacing[48]};
  margin-left: ${(props) => props.theme.spacing[20]};
`;

const StyledTd = styled.td`
  vertical-align: top;
  padding-right: ${(props) => props.theme.spacing[12]};
  padding-bottom: ${(props) => props.theme.spacing[20]};
`;

const StyledIcon = styled(Icon)`
  color: ${(props) => props.theme.color.primary[80]};
`;

const DarkText = styled.div`
  color: ${(props) => props.theme.color.neutral[100]};
  font: ${(props) => props.theme.typography.body.md.regular};
  display: inline;
`;

const LinkButton = styled(Button)`
  font: ${(props) => props.theme.typography.body.md.regular};
`;

const Content = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[90]};
`;

const ContentTitle = styled.div`
  font: ${(props) => props.theme.typography.heading['2xl']};
  margin-bottom: ${(props) => props.theme.spacing[12]};
`;

const ImageWrapper = styled.div`
  color: ${(props) => props.theme.color.success[40]};
  padding: ${(props) => props.theme.spacing[12]};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  max-height: 100%;
  gap: ${(props) => props.theme.spacing[16]};
  margin: ${(props) => props.theme.spacing[16]};
  margin-bottom: ${(props) => props.theme.spacing[96]};
`;

const PhoneNumber = styled.a`
  text-decoration: underline;
  display: inline;
  color: ${(props) => props.theme.color.primary[80]};
  font: ${(props) => props.theme.typography.body.md.semibold};
`;

interface AttendanceAllowanceSuccessProps {
  handlePrintPdf: () => void;
  isDownloading?: boolean;
  onBack?: () => void;
  name: string;
}

AttendanceAllowanceSuccess.displayName = 'AttendanceAllowanceSuccess';

export default AttendanceAllowanceSuccess;
